import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-relay';

import { SettingTypeInput } from '../../package/src/graphql/__generated__/UpdateSettingMutation.graphql';
import { COLOR_ICON, HEIGHT_ITEM } from '../const';
import { findItemFirstChild } from '../functions';
import { getMappingIdHideCollapse, getMappingIdIndex } from '../gantTransformData';
import UpdateSettingMutation from '../graphql/mutations/UpdateSettingMutation';
import useCommitSetting from '../hooks/useCommitSetting';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setMappingItemIdIndex, setterGant } from '../store/slices/gantSlice';
import { getExpandedItems } from '../table/functions';
import {
    ItemType,
    MappingItemIdHideCollapse,
    MappingItemIdIndexType,
    SettingGantDataType,
    SettingGantType,
} from '../types';

const TRIANGLE_PATH = `M 5 1.5 L 9 8.5 L 1 8.5 Z`;

interface IGantLeftHeaderBoxItemArrowCollapse {
    item: ItemType;
    offsetX: number;
    isCircleHidden?: boolean;
    cursorAreaOffset?: number;
}

const GantLeftHeaderBoxItemArrowCollapse: React.FC<IGantLeftHeaderBoxItemArrowCollapse> = ({
    item,
    offsetX,
    isCircleHidden,
    cursorAreaOffset,
}) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitUpdate] = useMutation(UpdateSettingMutation);
    const { getCommitSetting } = useCommitSetting();

    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const connectionSettingGant = useSelector<RootState, string>((state) => state.gant.connectionSettingGant);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const settingGant = useSelector<RootState, SettingGantType | null>((state) => state.gant.settingGant);
    const authorId = useSelector<RootState, string>((state) => state.gant.authorId);
    const expandItemIds = useSelector<RootState, string | null>((state) => state.gant.expandItemIds);

    const hideShowItem = (itemId: string, directionHide: boolean) => {
        const subItem = { ...itemsList[mappingItemIdIndex[itemId]] };
        subItem.hideForCollapse = directionHide;

        const result: ItemType[] = [subItem];
        if (subItem?.children?.length) {
            for (const child of subItem.children) {
                result.push(...hideShowItem(child, directionHide));
            }
        }
        return result;
    };

    const saveItemCollapseState = (mappingItemIdHideCollapse: MappingItemIdHideCollapse) => {
        return new Promise<void>((resolve) => {
            const newSettingGantData = {
                ...settingGant?.data,
                gantVisibleItems: {
                    [authorId]: mappingItemIdHideCollapse,
                },
            } as SettingGantDataType;

            const newSettingGantForServer = {
                ...settingGant,
                data: JSON.stringify(newSettingGantData),
            } as SettingTypeInput;

            commitUpdate(
                getCommitSetting(
                    newSettingGantForServer,
                    connectionSettingGant,
                    settingGant?.id,
                    () => {
                        console.log('settings save');
                        setTimeout(resolve);
                    },
                    true,
                ),
            );
        });
    };

    const updateCollapseState = (targetItems: ItemType[], directionHide: boolean) => {
        const allUpdatedItems: ItemType[] = [];
        targetItems.forEach((targetItem) => {
            targetItem.children?.forEach((childId) => {
                allUpdatedItems.push(...hideShowItem(childId, directionHide));
            });
        });

        const updatedItemIds = new Set(allUpdatedItems.map((item) => item.id));

        let index = 0;
        const newItems = itemsList
            .map((item) =>
                updatedItemIds.has(item.id) ? allUpdatedItems.find((updated) => updated.id === item.id)! : item,
            )
            .map((item) => ({
                ...item,
                idIndex: !item.hideForCollapse ? index++ : -1,
            }));

        const newMappingScheduleItemIdIndex = getMappingIdIndex(newItems);
        const newMappingItemIdHideCollapse = getMappingIdHideCollapse(newItems);

        saveItemCollapseState(newMappingItemIdHideCollapse).then(() => {
            dispatch(
                setterGantItems({
                    itemsList: newItems,
                }),
            );

            dispatch(
                setterGant({
                    countVisibleRow: getExpandedItems(newItems, newMappingScheduleItemIdIndex).filter(
                        (item) => !item.hideForCollapse,
                    ).length,
                    mappingItemIdHideCollapse: newMappingItemIdHideCollapse,
                    expandItemIds: null,
                }),
            );
            dispatch(setMappingItemIdIndex(newMappingScheduleItemIdIndex));
        });
    };

    useEffect(() => {
        if (!expandItemIds) {
            return;
        }

        const expandItems = expandItemIds
            .split(',')
            .map((itemId) => itemsList[mappingItemIdIndex[itemId]])
            .filter((item) => {
                const firstChild = findItemFirstChild('children', item, itemsList, mappingItemIdIndex);
                return firstChild?.hideForCollapse;
            });

        if (expandItems.length > 0) {
            updateCollapseState(expandItems, false);
        }
    }, [expandItemIds]);

    const handleClick = (item: ItemType) => (e: React.MouseEvent<SVGRectElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const firstChildId = findItemFirstChild('children', item, itemsList, mappingItemIdIndex)?.id;

        if (!firstChildId) {
            return;
        }

        const directionHide = !itemsList[mappingItemIdIndex[firstChildId]].hideForCollapse;

        updateCollapseState([item], directionHide);
    };

    return (
        <>
            {item.children?.length && (
                <g>
                    <g transform={`translate(${offsetX - 20}, ${(HEIGHT_ITEM / 100) * 35})`}>
                        {!isCircleHidden && <circle cx={5} cy={5} r="8" fill="white" cursor={'pointer'} />}
                        <path
                            d={TRIANGLE_PATH}
                            fill={COLOR_ICON}
                            transform={`rotate(${-30 + (findItemFirstChild('children', item, itemsList, mappingItemIdIndex)?.hideForCollapse ? 0 : 90)}, 5, 5)`}
                            cursor={'pointer'}
                        />
                    </g>
                    <rect
                        x={cursorAreaOffset}
                        y={0}
                        width={offsetX}
                        opacity={0.01}
                        height={HEIGHT_ITEM}
                        fill={'white'}
                        cursor="pointer"
                        onClick={handleClick(item)}
                    />
                </g>
            )}
        </>
    );
};

export default memo(GantLeftHeaderBoxItemArrowCollapse);
